import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  X,
  LayoutDashboard,
  Users,
  UserPlus,
  Phone,
  Calendar,
  AlertCircle,
  Clock,
  UserX,
  FileBarChart,
  LogOut,
  Building2,
  MessageSquareWarning,
  CalendarCheck,
  Send,
  Link2,
  ChevronRight,
  Target,
  Settings,
  TrendingUp,
  Upload,
  History
} from "lucide-react";
import { cn, apiRequest } from "../../lib/utils";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";

// Admin navigation with grouped sections - Separating Leads and SDR
const adminNavGroups = [
  {
    label: "Leads",
    items: [
      { icon: UserPlus, label: "All Leads", path: "/leads" },
      { icon: Phone, label: "Leads Follow-up", path: "/leads-follow-ups" }
    ]
  },
  {
    label: "SDR",
    items: [
      { icon: Clock, label: "Due", path: "/due", badge: "due" },
      { icon: AlertCircle, label: "Overdue", path: "/overdue", badge: "overdue" },
      { icon: Calendar, label: "Delayed", path: "/delayed", badge: "delayed" },
      { icon: UserX, label: "No Show", path: "/no-show", badge: "no-show" },
      { icon: Target, label: "SDR Follow-up", path: "/sdr-follow-ups" },
      { icon: Send, label: "No Show Campaign", path: "/no-show-campaign" }
    ]
  },
  {
    label: "Common",
    items: [
      { icon: CalendarCheck, label: "Bookings", path: "/bookings" },
      { icon: Users, label: "Customers", path: "/customers" },
      { icon: MessageSquareWarning, label: "Complaints", path: "/complaints" },
      { icon: History, label: "Calls Done", path: "/calls-done" }
    ]
  },
  {
    label: "Admin",
    items: [
      { icon: Building2, label: "Branches", path: "/branches" },
      { icon: Settings, label: "Branch Settings", path: "/branch-settings" },
      { icon: Users, label: "CRE Management", path: "/cre-management" },
      { icon: TrendingUp, label: "CRE Performance", path: "/cre-performance" },
      { icon: Link2, label: "API Integrations", path: "/settings/api-integrations" },
      { icon: Upload, label: "Data Import", path: "/settings/data-import" },
      { icon: FileBarChart, label: "Reports", path: "/reports" }
    ]
  }
];

// CRE navigation (no admin settings)
const creNavGroups = [
  {
    label: "Leads",
    items: [
      { icon: UserPlus, label: "My Leads", path: "/leads" },
      { icon: Phone, label: "Leads Follow-up", path: "/leads-follow-ups" }
    ]
  },
  {
    label: "SDR",
    items: [
      { icon: Clock, label: "Due", path: "/due", badge: "due" },
      { icon: AlertCircle, label: "Overdue", path: "/overdue", badge: "overdue" },
      { icon: Calendar, label: "Delayed", path: "/delayed", badge: "delayed" },
      { icon: UserX, label: "No Show", path: "/no-show", badge: "no-show" },
      { icon: Target, label: "SDR Follow-up", path: "/sdr-follow-ups" }
    ]
  },
  {
    label: "Common",
    items: [
      { icon: CalendarCheck, label: "Bookings", path: "/bookings" },
      { icon: Users, label: "My Customers", path: "/customers" },
      { icon: MessageSquareWarning, label: "Complaints", path: "/complaints" },
      { icon: History, label: "Calls Done", path: "/calls-done" },
      { icon: Upload, label: "Data Import", path: "/data-import" }
    ]
  }
];

const getBadgeStyle = (badge) => {
  switch(badge) {
    case "due": return "bg-yellow-100 text-yellow-800";
    case "overdue": return "bg-orange-100 text-orange-800";
    case "delayed": return "bg-blue-100 text-blue-800";
    case "no-show": return "bg-red-100 text-red-800";
    default: return "bg-gray-100 text-gray-800";
  }
};

export const MobileDrawer = ({ open, onClose }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const navGroups = isAdmin() ? adminNavGroups : creNavGroups;
  const [counts, setCounts] = useState({});

  useEffect(() => {
    if (open) {
      fetchCounts();
    }
  }, [open, user?.id]);

  const fetchCounts = async () => {
    try {
      const endpoint = isAdmin() 
        ? "/dashboard/admin" 
        : `/dashboard/cre/${user?.id}`;
      const data = await apiRequest(endpoint);
      setCounts({
        due: data.due_customers || data.due_count || 0,
        overdue: data.overdue_customers || data.overdue_count || 0,
        delayed: data.delayed_customers || data.delayed_count || 0,
        "no-show": data.no_show_customers || data.no_show_count || 0
      });
    } catch (error) {
      console.error("Failed to fetch counts:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    onClose();
  };

  const handleNavClick = (path) => {
    navigate(path);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 md:hidden animate-in fade-in duration-200"
        onClick={onClose}
        data-testid="drawer-overlay"
      />
      
      {/* Drawer */}
      <div 
        className="fixed left-0 top-0 bottom-0 w-72 bg-white z-[60] md:hidden shadow-2xl animate-in slide-in-from-left duration-250 flex flex-col"
        style={{ height: '100vh', maxHeight: '100vh' }}
        data-testid="mobile-drawer"
      >
        {/* Header - Fixed at top */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#ED1C24] flex items-center justify-center">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <div>
              <h1 className="font-heading font-bold text-base">Servall CRE-CRM</h1>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
                {isAdmin() ? "Admin Panel" : "CRE Panel"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            data-testid="close-drawer"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto overscroll-contain pb-20" style={{ WebkitOverflowScrolling: 'touch' }}>
          {/* Dashboard Link */}
          <div className="p-2">
            <NavLink
              to="/dashboard"
              onClick={() => handleNavClick("/dashboard")}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all",
                  isActive 
                    ? "bg-[#ED1C24] text-white shadow-md" 
                    : "text-gray-700 hover:bg-gray-100"
                )
              }
              data-testid="drawer-nav-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>Dashboard</span>
            </NavLink>
          </div>

          {/* Navigation Groups */}
          <nav className="pb-4">
            {navGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="px-2 mb-2">
                {/* Section Label */}
                <div className="px-4 py-2 mt-2">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    {group.label}
                  </span>
                </div>
                
                {/* Section Items */}
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => handleNavClick(item.path)}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-all",
                          isActive 
                            ? "bg-[#ED1C24] text-white shadow-md" 
                            : "text-gray-700 hover:bg-gray-100"
                        )
                      }
                      data-testid={`drawer-nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                    >
                      {({ isActive }) => (
                        <>
                          <div className="flex items-center gap-3">
                            <item.icon className="w-5 h-5" />
                            <span>{item.label}</span>
                          </div>
                          {item.badge && counts[item.badge] > 0 && (
                            <span className={cn(
                              "px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums",
                              isActive ? "bg-white/20 text-white" : getBadgeStyle(item.badge)
                            )}>
                              {counts[item.badge]}
                            </span>
                          )}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </div>

        {/* Fixed Bottom: User Profile & Logout */}
        <div className="flex-shrink-0 p-4 border-t border-gray-100 bg-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-full bg-[#ED1C24] text-white flex items-center justify-center font-bold text-lg">
              {user?.name?.charAt(0).toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full justify-center border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            onClick={handleLogout}
            data-testid="drawer-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>
    </>
  );
};

export default MobileDrawer;
